import { Permission } from '@bookora/shared';

import type { OnboardingStepResponse } from '@/api/generated/model';
import { useOnboarding } from '@/features/settings/hooks';
import { useCan } from '@/permissions/use-can';

import { setupStepHref } from './setup-step-href';

/**
 * «راه‌اندازی کلینیک» — ONE reading of the onboarding checklist for every
 * guidance surface: the dashboard card (`useSetupCardVisible`), the hub's
 * setup row, the `SetupStepBar` on a step's own page, the welcome's
 * postponement and a tour's «ادامهٔ راه‌اندازی» finish. They used to disagree
 * on who sees it (`settings.view` vs `settings.edit`), on whether a completed
 * checklist counts, and on where «ادامه» goes.
 *
 * `visible` = the viewer may read the checklist (`settings.view`, the
 * endpoint's own key), it is not marked complete, and a REQUIRED step is
 * still open. `next` is the server's own next step, else the first open
 * required one; `nextPath` is where «ادامهٔ راه‌اندازی» goes — the step's page
 * with its tour (`setupStepHref`), or the checklist itself when there is no
 * next step.
 */
export interface SetupProgress {
  visible: boolean;
  completed: number;
  total: number;
  percent: number;
  next: OnboardingStepResponse | null;
  nextPath: string;
  steps: readonly OnboardingStepResponse[];
}

/** The setup wizard, which opens on the first open required step (signup lands here). */
export const SETUP_CHECKLIST_PATH = '/setup';

const HIDDEN: SetupProgress = {
  visible: false,
  completed: 0,
  total: 0,
  percent: 0,
  next: null,
  nextPath: SETUP_CHECKLIST_PATH,
  steps: [],
};

export function useSetupProgress(): SetupProgress {
  const canView = useCan(Permission.SETTINGS_VIEW);
  const onboarding = useOnboarding();
  const data = onboarding.data;
  if (!canView || !data) return HIDDEN;
  const next =
    data.steps.find((step) => step.key === data.nextStepKey && !step.done) ??
    data.steps.find((step) => step.required && !step.done) ??
    null;
  const visible =
    !data.completedAt && data.totalRequired > 0 && data.completedRequired < data.totalRequired;
  return {
    visible,
    completed: data.completedRequired,
    total: data.totalRequired,
    percent: data.progressPercent,
    next,
    nextPath: next ? setupStepHref(next) : SETUP_CHECKLIST_PATH,
    steps: data.steps,
  };
}

/**
 * The checklist's `panelPath`s that the panel redirects on arrival (the
 * routes `app/onboarding-panelpath.test.tsx` locks): where the user actually
 * IS once a step's link has been followed.
 */
const SETTINGS_TAB_OF: Readonly<Record<string, string>> = {
  '/settings/clinic': 'clinic',
  '/settings/verification': 'verification',
  '/settings/schedule': 'hours',
  '/settings/rooms': 'rooms',
  '/settings/onboarding': 'onboarding',
};
const PATH_REDIRECTS: Readonly<Record<string, string>> = {
  '/inventory/receive': '/inventory',
};

/**
 * Whether the location is the page a step's `panelPath` leads to — before or
 * after the panel's redirect (`/settings/schedule` ⇒ `/settings?tab=hours`,
 * `/inventory/receive` ⇒ `/inventory`). A sub-path counts (`/staff/…`).
 */
export function isOnStepPage(panelPath: string, pathname: string, search: string): boolean {
  const bare = panelPath.split(/[?#]/)[0] ?? panelPath;
  if (pathname === bare || pathname.startsWith(`${bare}/`)) return true;
  const tab = SETTINGS_TAB_OF[bare];
  if (tab) return pathname === '/settings' && new URLSearchParams(search).get('tab') === tab;
  const redirected = PATH_REDIRECTS[bare];
  return redirected !== undefined && pathname === redirected;
}

/** The open (not done) step whose page this is, or `null`. Required steps first. */
export function openStepForLocation(
  steps: readonly OnboardingStepResponse[],
  pathname: string,
  search: string,
): OnboardingStepResponse | null {
  const here = steps.filter((step) => !step.done && isOnStepPage(step.panelPath, pathname, search));
  return here.find((step) => step.required) ?? here[0] ?? null;
}
